import { Box, BoxProps, useCallbackRef } from '@chakra-ui/react';
import { runIfFn } from '@chakra-ui/utils';
import { useVirtualizer } from '@tanstack/react-virtual';
import { difference } from 'lodash';
import { FC, ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';

import { ReverseScrollBox } from '@/components/core/ReverseScrollBox/ReverseScrollBox';
import { useChatDataContext } from '@/components/ui/shared/conversation/Chat/Chat.context';
import {
  estimateVritualItemHeight,
  getVisibleMessageSids,
} from '@/components/ui/shared/conversation/Chat/ChatList.utils';
import { IMessageLoaderProps } from '@/components/ui/shared/conversation/MessageLoader/MessageLoader';
import { INestedChatMessageProps } from '@/components/ui/shared/conversation/NestedChatMessage/NestedChatMessage';
import { useMessages } from '@/hooks/useMessages';
import useSession from '@/hooks/useSession';
import { findLastReplyIndex, manuallyRemoveMessage } from '@/utils/conversation/messageEvents';
import { defaultRangeExtractor } from '@/utils/conversation/rangeExtractor';
import { IConversationMessage } from '@/utils/conversation/types';

import { INewMessagesNotificationButtonProps } from '../NewMessagesNotificationButton/NewMessagesNotificationButton';
import { IScrollToBottomButtonProps } from '../ScrollToBottomButton/ScrollToBottomButton';

interface IChatListProps extends Omit<BoxProps, 'children'> {
  scrollButtonSlot: (props: IScrollToBottomButtonProps) => ReactElement;
  notificationButtonSlot: (props: INewMessagesNotificationButtonProps) => ReactElement;
  messageLoaderSlot: (props: IMessageLoaderProps) => ReactElement;
  children: (props: INestedChatMessageProps) => ReactElement;
  onMessageSend?: (message: IConversationMessage) => void;
}

export const ChatList: FC<IChatListProps> = ({
  children,
  scrollButtonSlot,
  notificationButtonSlot,
  messageLoaderSlot,
  onMessageSend,
  ...props
}) => {
  const parentRef = useRef<HTMLDivElement>();
  const { user } = useSession();

  const isScrolling = useRef(false);
  const isScrolledToBottom = useRef(true);
  const isScrolledToTop = useRef(false);

  const isChatLocked = useRef(false);

  // will hold state of messages while chat is frozen
  const cachedMessagesRef = useRef([]);
  // hold sids of messages in users viewport
  const visibleMessagesRef = useRef([]);
  // holds indexes of unread messages
  const unreadMessagesRef = useRef<Array<string>>([]);

  const { conversation } = useChatDataContext();

  const updateLockState = useCallbackRef(() => {
    isChatLocked.current = !isScrolledToTop.current && !isScrolledToBottom.current && !isScrolling.current;
  });

  const scrollToBottom = useCallbackRef(() => {
    if (parentRef.current) {
      parentRef.current.scrollTo({ top: 0 });
    }
  });

  const { messages: rawMessages = [], loadMore, isLoadingMore, isReachingEnd, isLoadingMessages } = useMessages(
    conversation,
    {
      pageSize: 10,
      onMessageAdded: (addedMessage: IConversationMessage) => {
        const { parentMessageId } = addedMessage.attributes;

        onMessageSend?.(addedMessage);

        if (isChatLocked.current) {
          const isParentVisible = visibleMessagesRef.current.includes(parentMessageId);

          if (isParentVisible) {
            const lastReplyIndex = findLastReplyIndex(cachedMessagesRef.current, parentMessageId);

            // add message to cache
            cachedMessagesRef.current.splice(lastReplyIndex, 0, addedMessage);
          }

          if (addedMessage.author === user.email && !parentMessageId) {
            scrollToBottom();
          }
        }

        // dont update unread count if user is the author
        if (!addedMessage.attributes?.parentMessageId && addedMessage.author !== user.email) {
          unreadMessagesRef.current.push(addedMessage.sid);
        }
      },
      onMessageRemoved: (removedMessage: IConversationMessage) => {
        if (isChatLocked.current) {
          const isThreadVisible = visibleMessagesRef.current.includes(
            removedMessage.attributes?.parentMessageId || removedMessage.sid
          );

          if (isThreadVisible) {
            manuallyRemoveMessage({ message: removedMessage, cachedMessagesRef });
          }
        }

        unreadMessagesRef.current = unreadMessagesRef.current.filter((sid) => removedMessage.sid !== sid);
      },
    }
  );

  const messages = useMemo(() => {
    if (isChatLocked.current) {
      return cachedMessagesRef.current;
    }

    cachedMessagesRef.current = rawMessages;
    return rawMessages;
  }, [rawMessages]);

  const onScroll = useCallbackRef(() => {
    if (parentRef.current) {
      const scrollPadding = 20; //px

      const { scrollTop, scrollHeight, clientHeight } = parentRef.current;
      const scrollOffset = Math.ceil(scrollTop) + clientHeight;

      isScrolling.current = true;

      setTimeout(() => {
        isScrolling.current = false;
        updateLockState();
      }, 120);

      isScrolledToTop.current = scrollOffset >= scrollHeight;
      isScrolledToBottom.current = scrollTop === 0;

      updateLockState();

      if (scrollOffset >= scrollHeight - scrollPadding) {
        // it's actually bottom but chat is reversed
        loadMore();
      }
    }
  });

  const rangeExtractor = useCallback(
    (range) => {
      const visibleIndexes = defaultRangeExtractor(range);

      visibleMessagesRef.current = getVisibleMessageSids(visibleIndexes, messages);
      // remove visible messages from unread messages
      unreadMessagesRef.current = difference(unreadMessagesRef.current, visibleMessagesRef.current);

      return visibleIndexes;
    },
    [messages]
  );

  const rowVirtualizer = useVirtualizer({
    count: messages?.length,
    paddingEnd: isLoadingMore && !isReachingEnd ? 100 : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: useCallback((index) => estimateVritualItemHeight(messages[index]), [messages]),
    getItemKey: (index: number) => messages[index].sid,
    rangeExtractor,
  });

  // load more if one page is not enought to fill the screen
  // if that happens user is unable to scroll to top
  // and trigger next page

  useEffect(() => {
    if (parentRef.current) {
      const hasVerticalScrollbar = rowVirtualizer.getTotalSize() > parentRef.current.clientHeight;

      if (!hasVerticalScrollbar && !isReachingEnd) {
        loadMore();
      }
    }
  }, [isReachingEnd, rowVirtualizer, loadMore]);

  const unreadMessages = unreadMessagesRef.current;

  if (isLoadingMessages) {
    return (
      <Box position="relative" height="full" {...props}>
        <Box bg="white" height="full" px="20px" overflowY="auto">
          {runIfFn(messageLoaderSlot, {
            size: 10,
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box position="relative" height="full" {...props}>
      <ReverseScrollBox
        bg="white"
        height="full"
        pt={1}
        overflowY="auto"
        onScroll={onScroll}
        ref={parentRef}
        transform="scaleY(-1)"
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            return (
              <Box
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px) scaleY(-1)`,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {runIfFn(children, {
                  message: messages[virtualRow.index],
                  ref: virtualRow.measureElement,
                })}
              </Box>
            );
          })}
        </div>
        {runIfFn(notificationButtonSlot, {
          scrollToLastReadMessage: (index) => rowVirtualizer.scrollToIndex(index, { align: 'center' }),
          messages: rawMessages,
          unreadMessages,
        })}
      </ReverseScrollBox>
      {runIfFn(scrollButtonSlot, {
        onClick: scrollToBottom,
        disabled: isScrolledToBottom.current,
      })}
    </Box>
  );
};
